import React, { forwardRef, useRef } from "react"
import { Icon } from "@kit/design/icon"
import Stack from "@kit/design/stack"
import { useMediaBreakpoint } from "@kit/design/use-media-breakpoint"
import pluralize from "pluralize"
import { Menu, MenuButton, MenuList } from "@reach/menu-button"
import { DiscoveryHub } from "app/discovery-hub"
import { Notifications } from "../../../../engines/notifications/javascript"
import { Avatar } from "core/avatar"
import { settings } from "./utils/menu-definitions"
import { SettingsItem } from "./utils/settings-item"
import { useAccountOptions } from "./utils/use-account-options"
import { TrialNav } from "./trial-nav"
import { UpgradeCallout } from "./upgrade-callout/index"
import { FreePlanUpgradeButton } from "./free-plan-upgrade-button"
import { useVerticalNavOptIn } from "./utils/use-vertical-nav-optin"
import { useOptInHint } from "./utils/use-optin-hint"
import { useSunsetHint } from "./utils/use-sunset-hint"
import { currentSunsetWeek } from "./utils/sunset-hint-content"
import { OLD_NAV_HINT } from "./utils/optin-hint-content"
import { analytics } from "utils/analytics"

import "./settings-nav.css"

const SettingsButton = forwardRef(({ isExpanded, userInfo, onClick }, ref) => {
  const iconTransform = isExpanded && "transform -rotate-180"
  const activeButtonStyles = isExpanded
    ? "active text-black"
    : "hover:text-black hover:bg-warm-white-100 focus:text-black"

  return (
    <MenuButton
      ref={ref}
      className={`inline-flex items-center focus:outline-none gap-2 p-2 rounded nav-hover relative focus:bg-blue-600 ${activeButtonStyles} text-black focus:bg-warm-white-200`}
      style={{ padding: "4px 4px 4px 12px" }}
      title={userInfo.name}
      onClick={onClick}
    >
      <Avatar email={userInfo.email} />
      <span
        className="align-middle font-semibold truncate"
        style={{ maxWidth: 200 }}
      >
        {userInfo.name}
      </span>
      <Icon name="down" className={`duration-200 ${iconTransform}`} />
    </MenuButton>
  )
})
SettingsButton.displayName = "SettingsButton"

export function SettingsNav({ userInfo }) {
  const settingOptions = useAccountOptions(userInfo)
  const {
    optInEnabled,
    onVerticalNav,
    shouldShowModal,
    shouldShowRevertModal,
    shouldShowOldNavHint,
    enableVerticalNav,
    dismissHint
  } = useVerticalNavOptIn()
  const sunset = useSunsetHint()
  const navRef = useRef(null)
  const daysLeft = pluralize("day", userInfo.trial_days_remaining, true)

  // SettingsNav only renders at lg+; below that the old nav collapses into
  // MobileNav, which anchors the hint itself.
  const onLargeScreen = useMediaBreakpoint("lg")

  // Until the first sunset hint is available, keep the original one-time
  // coachmark for creators who chose the old nav from the greeting modal. This
  // and the sunset hint below are time-disjoint (this only before the first
  // sunset week, the sunset hint only from that week on), so they never stack.
  useOptInHint({
    open: shouldShowOldNavHint && onLargeScreen && currentSunsetWeek() == null,
    anchorRef: navRef,
    onDismiss: dismissHint,
    ...OLD_NAV_HINT,
    position: "bottom",
    align: "end"
  })

  // Weekly reminder that the old nav is going away, anchored to the settings
  // button. Only for creators still on the old nav (optInEnabled + !onVerticalNav),
  // and held back while the greeting or revert modal is still pending so the
  // coachmark never stacks on a modal.
  useOptInHint({
    open:
      sunset.open &&
      optInEnabled &&
      !onVerticalNav &&
      !shouldShowModal &&
      !shouldShowRevertModal &&
      onLargeScreen,
    anchorRef: navRef,
    onDismiss: sunset.dismiss,
    title: sunset.title,
    description: sunset.description,
    position: "bottom",
    align: "end"
  })

  function handleSettingsClick(isExpanded) {
    // Opening the settings menu is where "Switch to the new navigation" lives,
    // so dismiss whichever hint is pointing here (only one shows at a time).
    dismissHint()
    sunset.dismiss()

    if (!isExpanded) {
      analytics.track(
        "Element",
        "Clicked",
        {
          element_type: "button",
          name: "nav_section_settings",
          text: "Settings",
          location: "settings_nav"
        },
        { transport: "beacon" }
      )
    }
  }

  return (
    <div className="hidden lg:flex items-center gap-2 relative">
      <DiscoveryHub />
      <Notifications path={"/notifications"} />
      <TrialNav userInfo={userInfo} daysLeft={daysLeft} />
      <FreePlanUpgradeButton userInfo={userInfo} />
      <Menu>
        {({ isExpanded }) => (
          <>
            <SettingsButton
              ref={navRef}
              isExpanded={isExpanded}
              userInfo={userInfo}
              onClick={() => handleSettingsClick(isExpanded)}
            />
            <MenuList className="rounded shadow-lg settings-menu bg-white mt-2 relative z-10">
              <UpgradeCallout userInfo={userInfo} />
              <Stack spacing={2} className="mb-2">
                {Object.entries(
                  settings(userInfo.current_admin, settingOptions, {
                    enabled: optInEnabled,
                    onSelect: enableVerticalNav
                  })
                ).map(([menu, options]) => (
                  <SettingsItem key={menu} {...options} />
                ))}
              </Stack>
            </MenuList>
          </>
        )}
      </Menu>
    </div>
  )
}
